import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { FloatingLabelDatePicker } from '@/shared/components/custom/form-field/FloatingLabelDatePicker';
import { useToast } from '@/shared/hooks/useToast';
import { useSetMemberHireDate } from '../hooks/useSetMemberHireDate';

interface SetHireDateDialogProps {
  member: { userId: string; displayName?: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * `YYYY-MM-DD` from an ISO instant's *local* calendar fields — never by
 * slicing the ISO string itself, which encodes UTC and would shift the day
 * by one in negative-offset timezones. `SetMemberHireDateRequest.hireDate`
 * is backend-typed `format: date`, so the picker's full ISO datetime value
 * must be narrowed down to just the date before sending.
 */
function toLocalDateOnly(isoValue: string): string {
  const date = new Date(isoValue);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * TimeOff requires a hire date on file before it can open an entitlement
 * cycle for a member (`TimeOff.MemberHasNoHireDate` otherwise) — this is the
 * only place in the product that sets it, since `MemberDto` doesn't return
 * the current value to prefill from.
 */
export function SetHireDateDialog({ member, open, onOpenChange }: SetHireDateDialogProps) {
  const { t } = useTranslation('members');
  const { toast } = useToast();
  const setHireDate = useSetMemberHireDate();
  const [date, setDate] = useState<string | null>(null);

  const name = member.displayName || member.userId;

  async function handleSave() {
    if (!date) return;
    try {
      await setHireDate.mutateAsync({
        userId: member.userId,
        payload: { hireDate: toLocalDateOnly(date) },
      });
      toast.success({
        title: t('toast.hireDateSet'),
        description: t('toast.hireDateSetDesc', { name }),
      });
      onOpenChange(false);
    } catch {
      // global handler shows error toast
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="members-set-hire-date-dialog">
        <DialogHeader>
          <DialogTitle>{t('actions.setHireDate')}</DialogTitle>
          <DialogDescription>{t('setHireDate.description', { name })}</DialogDescription>
        </DialogHeader>

        <FloatingLabelDatePicker
          id="members-hire-date"
          label={t('setHireDate.label')}
          value={date}
          onChange={setDate}
          maxDate={new Date()}
          data-testid="members-hire-date-input"
        />

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="members-hire-date-cancel-button"
          >
            {t('setHireDate.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!date || setHireDate.isPending}
            data-testid="members-hire-date-save-button"
          >
            {setHireDate.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('setHireDate.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
